/*
Copyright (c) 2026 Wind River Systems, Inc.

The right to copy, distribute, modify, or otherwise make use of this software may be licensed only pursuant to the terms of an applicable Wind River license agreement.
*/
import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import {
  CommItem,
  Entitlement,
  Product,
  ProductArtifact,
  Release,
  ReleaseVersion,
  EntitledRelease,
  DownloadHistory,
  UserProduct
} from 'src/app/models/products';
import { ConfigService } from '@windriver/authentication';
import { catchError, map, tap } from 'rxjs/operators';
import { SearchUserResult, Impersonation } from 'src/app/models/user';
import { UiReleaseNote } from 'src/app/components/products/products.models';
import { CurrentUserService } from '../current-user/current-user.service';
import { ConfigState } from '../../models/config-state';

// Calls endpoints for customers to see their products.
// Calls endpoints for internal users to add or remove products, or impersonate a customer
@Injectable({
  providedIn: 'root'
})
export class ProductsService {
  private productServiceBaseUrl: string;
  public impersonationEmitter = new EventEmitter<boolean>();
  public impersonatedUser: string;

  //default headers
  readonly productServiceHeaders = {
    'Content-Type': 'application/json'
  };

  readonly httpOptions = {
    headers: new HttpHeaders(this.productServiceHeaders),
    params: undefined
  };

  constructor(private http: HttpClient, private configService: ConfigService, currentUserService: CurrentUserService) {
    this.configService.configState.subscribe((config: ConfigState) => {
      if (config) {
        this.productServiceBaseUrl = config.apiUrl;
      }
    });

    this.getImpersonation(currentUserService.getUsername()).subscribe();
  }

  get impersonatedUsername() {
    return this.impersonatedUser;
  }

  // Note that the back end can return a 200 http status code with a JSON body that includes "status": "error".
  // RxJS will see that in the JSON and land us in the catchError(...) case. This is while we catch the error
  // and return the body of it anyway.
  private getErrorWithHandling<T>(url: string, params?: any): Observable<T> {
    const options = {
      ...this.httpOptions
    };
    if (params) {
      options.params = params;
    }

    return this.http.get<T>(url, options).pipe(
      catchError((error) => {
        console.log(`Error getting from ${url} : ${JSON.stringify(error)}`);
        return of(error);
      })
    );
  }

  private getOrThrow<T>(url: string, params?: any): Observable<T> {
    const options = { ...this.httpOptions };
    if (params) {
      options.params = params;
    }
    return this.http.get<T>(url, options);
  }

  private postErrorWithHandling<T>(url: string, body: any): Observable<T> {
    return this.http.post<T>(url, body, this.httpOptions).pipe(
      catchError((error) => {
        console.log(`Error posting to ${url} : ${JSON.stringify(error)}`);
        return of(error);
      })
    );
  }

  private deleteErrorWithHandling<T>(url: string, body?: any): Observable<T> {
    const deleteHttpOptions = {
      headers: this.httpOptions.headers,
      params: this.httpOptions.params,
      body
    };
    return this.http.request<T>('delete', url, deleteHttpOptions).pipe(
      catchError((error) => {
        console.log(`Error posting to ${url} : ${JSON.stringify(error)}`);
        return of(error);
      })
    );
  }

  getProducts(): Observable<Product[]> {
    const url = this.productServiceBaseUrl + '/v1/entitlement/products';
    return this.getErrorWithHandling(url);
  }

  getUserEntitlements(userName: string, impersonatedUserName?: string): Observable<Entitlement[]> {
    const url = this.productServiceBaseUrl + `/v1/entitlement/${userName}`;
    return this.getErrorWithHandling(
      url,
      impersonatedUserName ? { impersonatedCustomerUserName: impersonatedUserName } : {}
    );
  }

  getUserEntitledReleases(userName: string, impersonatedUserName?: string): Observable<EntitledRelease[]> {
    const url = this.productServiceBaseUrl + `/v1/entitlement/${userName}/releases`;
    return this.getOrThrow(url, impersonatedUserName ? { impersonatedCustomerUserName: impersonatedUserName } : {});
  }

  getDownloadHistory(
    releaseId: number,
    userName: string,
    impersonatedUserName?: string
  ): Observable<DownloadHistory[]> {
    const url = this.productServiceBaseUrl + `/v1/entitlement/download-history/${userName}/${releaseId}`;
    return this.getErrorWithHandling<DownloadHistory[]>(
      url,
      impersonatedUserName ? { impersonatedCustomerUserName: impersonatedUserName } : {}
    );
  }

  getUserEntitledReleaseDetails(
    releaseId: number,
    userName: string,
    impersonatedUserName?: string,
    release_eng_name_tag?: string
  ): Observable<Entitlement[]> {
    const url =
      this.productServiceBaseUrl +
      `/v1/entitlement/${userName}/releases/${releaseId}/comm-items?release_eng_name_tag=${release_eng_name_tag}`;
    return this.getErrorWithHandling(
      url,
      impersonatedUserName ? { impersonatedCustomerUserName: impersonatedUserName } : {}
    );
  }

  getUserOfflineRequestLogs(userName: string, impersonatedUserName?: string): Observable<Entitlement[]> {
    const url = this.productServiceBaseUrl + `/v1/tools/getOfflineRequestLogs/${userName}`;
    return this.getErrorWithHandling(
      url,
      impersonatedUserName ? { impersonatedCustomerUserName: impersonatedUserName } : {}
    );
  }

  getReleases(product: Product): Observable<Release[]> {
    const url = this.productServiceBaseUrl + `/v1/entitlement/product/${product.id}/releases`;
    return this.getErrorWithHandling(url);
  }

  getReleaseNotes(releaseId: number): Observable<UiReleaseNote[]> {
    const url = this.productServiceBaseUrl + `/v1/entitlement/releases/${releaseId}/notes`;
    return this.getErrorWithHandling(url);
  }

  getCommItems(product: Product, release: Release): Observable<CommItem[]> {
    const url = this.productServiceBaseUrl + `/v1/entitlement/releases/${release.id}/bundles`;
    return this.getErrorWithHandling(url);
  }

  getReleaseVersions(releaseId: number): Observable<ReleaseVersion[]> {
    const url = this.productServiceBaseUrl + `/v1/entitlement/release/spins/${releaseId}`;
    return this.getErrorWithHandling(url);
  }

  addtoMyProducts(userName: string, commItems: CommItem[], selectedRelease: Release): Observable<any> {
    return this.postErrorWithHandling(this.productServiceBaseUrl + `/v1/entitlement/${userName}/bundles`, {
      commItems,
      selectedRelease
    });
  }

  acceptLicenseAgreement(userName: string, releaseId: number, urlId: number) {
    const body = {
      releases_id: releaseId,
      url_id: urlId,
      agreement_choice: true
    };
    return this.postErrorWithHandling(
      this.productServiceBaseUrl + `/v1/entitlement/${userName}/license_agreement`,
      body
    );
  }

  removeReleaseFromMyProducts(
    userName: string,
    releaseId: number,
    engNameTag?: string
  ): Observable<Map<string, Release[]> & Omit<Impersonation, 'impersonatedCustomerUserName'>> {
    const body: { releaseId: number; engNameTag?: string } = { releaseId };
    if (engNameTag) {
      body.engNameTag = engNameTag;
    }
    return this.deleteErrorWithHandling(this.productServiceBaseUrl + `/v1/entitlement/${userName}/bundles`, body);
  }

  getImpersonation(userName: string): Observable<Impersonation> {
    return this.getErrorWithHandling<Impersonation>(
      this.productServiceBaseUrl + `/v1/tools/tozny/impersonation/${userName}`
    ).pipe(
      tap((res) => {
        this.impersonatedUser = res.impersonatedCustomerUserName;
        this.impersonationEmitter.emit(!!res.impersonatedCustomerUserName);
      })
    );
  }

  startImpersonation(userName: string, userToImpersonate: string): Observable<Impersonation> {
    return this.postErrorWithHandling<Impersonation>(
      this.productServiceBaseUrl + `/v1/tools/tozny/impersonation/${userName}`,
      {
        customerUserName: userToImpersonate
      }
    ).pipe(
      tap(() => {
        this.impersonationEmitter.emit(true);
      })
    );
  }

  stopImpersonation(userName: string): Observable<Impersonation> {
    return this.deleteErrorWithHandling<Impersonation>(
      this.productServiceBaseUrl + `/v1/tools/tozny/impersonation/${userName}`
    ).pipe(
      tap(() => {
        this.impersonationEmitter.emit(false);
      })
    );
  }

  // TODO - Service not implemented. Returns hard coded values.
  getArtifacts(): Observable<ProductArtifact[]> {
    return of(this.cannedArtifacts);
  }

  // TODO - Service not implemented.
  searchCustomers(searchTerm: string): Observable<SearchUserResult[]> {
    return this.getErrorWithHandling(this.productServiceBaseUrl + `/v1/entitlement/users/find/${searchTerm}`);
  }

  getCustomerCommItems(userName: string): Observable<CommItem[]> {
    return this.getErrorWithHandling(this.productServiceBaseUrl + `/v1/entitlement/${userName}/bundles`);
  }

  getOnlineInstaller(
    userName: string,
    hostEngName: string,
    productName: string,
    prereleaseTagName: string | null,
    impersonatedCustomerUserName: string
  ) {
    const url = this.productServiceBaseUrl + `/v1/online-installers/${userName}`;
    const headers = new HttpHeaders(this.productServiceHeaders);
    return this.http
      .post(
        url,
        {
          hostEngName,
          productName,
          prereleaseTagName,
          impersonatedCustomerUserName
        },
        { headers, responseType: 'blob', observe: 'events', reportProgress: true }
      )
      .pipe(
        catchError((error) => {
          console.log(`Error posting to ${url} : ${JSON.stringify(error)}`);
          return of(error);
        })
      );
  }

  getOfflineInstaller(
    userName: string,
    releaseEngName: string,
    archiveChoice: string,
    releaseSelectedVersionId: string,
    releaseName: string,
    impersonatedCustomerUserName: string,
    isGit: boolean = false
  ) {
    const url = this.productServiceBaseUrl + `/v1/offline-installers/create-installer/${userName}`;
    const body = {
      engNameTag: releaseEngName,
      archiveChoice,
      spinDbId: releaseSelectedVersionId,
      productName: releaseName,
      isGit
    };
    if (typeof impersonatedCustomerUserName !== 'undefined') {
      body['impersonatedCustomerUserName'] = impersonatedCustomerUserName;
    }
    return this.postErrorWithHandling(url, body);
  }

  downloadOfflineInstaller(url: string): Observable<{ blob: Blob; filename: string; percent?: number }> {
    return this.http.get(url, { responseType: 'blob', observe: 'events', reportProgress: true }).pipe(
      map((event) => {
        if (event.type === HttpEventType.DownloadProgress) {
          return {
            blob: null,
            filename: null,
            percent: event.total ? Math.round((100 * event.loaded) / event.total) : 0
          };
        }
        const response = event as HttpResponse<Blob>;
        const disposition = response.headers?.get('Content-Disposition') ?? '';
        const match = disposition.match(/filename="?([^"]+)"?/);
        return { blob: response.body as Blob, filename: match ? match[1] : 'download.zip', percent: 100 };
      }),
      catchError((error) => {
        console.log(`Error downloading from ${url} : ${JSON.stringify(error)}`);
        return of(error);
      })
    );
  }

  getOfflineDownloadToken(): Observable<string> {
    const url = this.productServiceBaseUrl + '/v1/tools/offline-installers/download-token';
    return this.http.get<{ token: string }>(url, this.httpOptions).pipe(
      map((res) => res.token),
      catchError((error) => {
        console.log(`Error fetching download token: ${JSON.stringify(error)}`);
        return of(null);
      })
    );
  }

  getUserProducts(username: string): Observable<UserProduct[]> {
    const url = this.productServiceBaseUrl + `/v1/entitlement/${username}/products`;
    return this.getErrorWithHandling(url);
  }

  // ---
  // Below here are hard-coded things that need to be replaced with proper queries to the backend.
  // ---

  private readonly cannedArtifacts: ProductArtifact[] = [
    { name: 'EdgeSync-Agent-4_0', url: 'https://0.0.0.0:999999/nonexistent' },
    { name: 'EdgeSync-Differential-System-4_0', url: 'http://0.0.0.0:999999/nonexistent' },
    { name: 'EdgeSync-Differential-System-Legal-Notices-4_0', url: 'https://0.0.0.0:999999/nonexistent' },
    { name: 'EdgeSync-Documentation-4_0', url: 'https://0.0.0.0:999999/nonexistent' }
  ];

  // private readonly cannedCommItems: CommItem[] = [
  //   { description: 'Workbench 4 System Analysis for VxWorks 7, Time Based', comm_item_id: '500-180905', id: 100, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'VxWorks 7 Core Platform, Time Based', comm_item_id: '500-180907', id: 101, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'VxWorks 7 QuickStart Galileo ', comm_item_id: '500-183435', id: 102, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'VxWorks 7 QuickStart BeagleBoneBlack ', comm_item_id: '500-183436', id: 103, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'VxWorks Core Platform (includes 6.x & 7), Time Based ', comm_item_id: '500-183706', id: 104, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'VxWorks 7 Core Platform, Time Based', comm_item_id: '500-184431', id: 105, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'Wind River General Purpose Platform VxWorks Edition 6, Time' , comm_item_id: '500-185743', id: 106, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'VxWorks, Time Based', comm_item_id: '500-186351', id: 107, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'VxWorks 7 Plus Platform, Time Based', comm_item_id: '500-184433', id: 108, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'VxWorks 6 Plus Platform, Time Based', comm_item_id: '500-185574', id: 109, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'VxWorks 7, Time Based', comm_item_id: '500-186274', id: 110, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'Security Profile for VxWorks 7, Time Based ', comm_item_id: '500-186427', id: 111, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'Workbench 4 System Analysis for VxWorks 7, Time Based', comm_item_id: '500-186752', id: 112, expiration_date: '2021-12-21T08:00:00.000Z' },
  //   { description: 'VxWorks 7, Time Bas', comm_item_id: '500-187556', id: 113, expiration_date: '2021-12-21T08:00:00.000Z' },
  // ];
}
